/**
 * Centralized Logger Utility
 *
 * Provides environment-based logging with different levels and categories.
 * Logs are controlled by environment variables and can be easily toggled.
 *
 * Usage:
 * import { logger } from '../utils/logger';
 *
 * logger.info('User logged in', { userId: 123 });
 * logger.error('API call failed', error, { endpoint: '/api/users' });
 * logger.debug('Component rendered', { component: 'UserProfile' });
 */

// Log levels in order of severity
const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
};

// App prefix for all log lines (easy to filter in console)
const APP_LOG_PREFIX = "AddoShareTeams";

// localStorage keys
const STORAGE_KEYS = {
  LOG_LEVEL: "ADDO_SHARE_LOG_LEVEL",
  ENABLE_LOGGING: "ADDO_SHARE_ENABLE_LOGGING",
};

// Default log level based on environment
const getDefaultLogLevel = () => {
  if (process.env.NODE_ENV === "production") {
    return LOG_LEVELS.ERROR; // Only errors in production
  }
  if (process.env.NODE_ENV === "development") {
    return LOG_LEVELS.DEBUG; // Debug level in development
  }
  return LOG_LEVELS.INFO; // Default to info
};

// Get log level from localStorage override or environment variable
const getLogLevel = () => {
  const localLogLevel = localStorage.getItem(STORAGE_KEYS.LOG_LEVEL);
  if (localLogLevel) {
    const levelMap = {
      ERROR: LOG_LEVELS.ERROR,
      WARN: LOG_LEVELS.WARN,
      INFO: LOG_LEVELS.INFO,
      DEBUG: LOG_LEVELS.DEBUG,
    };
    return levelMap[localLogLevel] || LOG_LEVELS.ERROR;
  }
  const envLevel = process.env.ADDO_SHARE_LOG_LEVEL?.toUpperCase();
  return LOG_LEVELS[envLevel] !== undefined ? LOG_LEVELS[envLevel] : getDefaultLogLevel();
};

// Check if logging is enabled (localStorage override or environment)
const getLoggingEnabled = () => {
  const localEnabled = localStorage.getItem(STORAGE_KEYS.ENABLE_LOGGING);
  if (localEnabled !== null) {
    return localEnabled === "true";
  }
  return process.env.NODE_ENV !== "production" || process.env.ADDO_SHARE_ENABLE_LOGGING === "true";
};

// Check if logging is enabled for a specific level
const shouldLog = (level) => {
  const isEnabled = getLoggingEnabled();
  const currentLevel = getLogLevel();
  const localEnabled = localStorage.getItem(STORAGE_KEYS.ENABLE_LOGGING);
  if (localEnabled === "true") {
    return true;
  }
  return isEnabled && level <= currentLevel;
};

// Get caller file and line info (development only)
const getCallerInfo = () => {
  if (process.env.NODE_ENV !== "development") {
    return "";
  }
  try {
    const stack = new Error().stack;
    const stackLines = stack.split("\n");
    for (let i = 3; i < stackLines.length; i++) {
      const line = stackLines[i];
      if (line.includes("logger.js") || line.includes("node_modules")) {
        continue;
      }
      const match = line.match(/\((.+):(\d+):(\d+)\)/) || line.match(/at\s+[^(]*\((.+):(\d+):(\d+)\)/);
      if (match) {
        const filePath = match[1];
        const lineNumber = match[2];
        let fileName = filePath.replace(/^.*\/\/\.\//g, "");
        fileName = fileName.replace(/^src\//, "");
        const parts = fileName.split("/");
        const displayName =
          parts.length > 1 ? `${parts[parts.length - 2]}/${parts[parts.length - 1]}` : parts[parts.length - 1];
        return ` [${displayName}:${lineNumber}]`;
      }
    }
  } catch (err) {
    console.error("Failed to get caller info", err);
  }
  return "";
};

// Format log message with app prefix, timestamp and context
const formatMessage = (level, category, message) => {
  const timestamp = new Date().toISOString();
  const levelName = Object.keys(LOG_LEVELS)[level];
  const callerInfo = getCallerInfo();
  const prefix = `[${APP_LOG_PREFIX}] [${timestamp}] [${levelName}]${callerInfo}`;
  if (category) {
    return `${prefix} [${category}] ${message}`;
  }
  return `${prefix} ${message}`;
};

const consoleMethods = {
  [LOG_LEVELS.ERROR]: (message, data) => {
    console.group(`🚨 ${message}`);
    if (data) console.error(data);
    console.groupEnd();
  },
  [LOG_LEVELS.WARN]: (message, data) => {
    console.group(`⚠️ ${message}`);
    if (data) console.warn(data);
    console.groupEnd();
  },
  [LOG_LEVELS.INFO]: (message, data) => {
    console.group(`ℹ️ ${message}`);
    if (data) console.info(data);
    console.groupEnd();
  },
  [LOG_LEVELS.DEBUG]: (message, data) => {
    console.group(`🛠️ ${message}`);
    if (data) console.log(data);
    console.groupEnd();
  },
};

class Logger {
  constructor() {
    this.currentLevel = getLogLevel();
    this.isEnabled = getLoggingEnabled();
  }

  _log(level, category, message, data = null) {
    if (!this.isEnabled || !shouldLog(level)) {
      return;
    }
    const formattedMessage = formatMessage(level, category, message);
    const consoleMethod = consoleMethods[level];
    if (consoleMethod) {
      consoleMethod(formattedMessage, data);
    } else {
      console.log(formattedMessage, data);
    }
  }

  error(message, data = null, category = null) {
    this._log(LOG_LEVELS.ERROR, category, message, data);
  }

  warn(message, data = null, category = null) {
    this._log(LOG_LEVELS.WARN, category, message, data);
  }

  info(message, data = null, category = null) {
    this._log(LOG_LEVELS.INFO, category, message, data);
  }

  debug(message, data = null, category = null) {
    this._log(LOG_LEVELS.DEBUG, category, message, data);
  }

  api(method, url, data = null, category = "API") {
    this.info(`${method.toUpperCase()} ${url}`, data, category);
  }

  auth(message, data = null) {
    this.info(message, data, "AUTH");
  }

  redux(action, data = null) {
    this.debug(`Redux Action: ${action}`, data, "REDUX");
  }

  component(componentName, message, data = null) {
    this.debug(`${componentName}: ${message}`, data, "COMPONENT");
  }

  performance(operation, duration, data = null) {
    this.info(`Performance: ${operation} took ${duration}ms`, data, "PERFORMANCE");
  }

  group(label) {
    if (this.isEnabled && shouldLog(LOG_LEVELS.DEBUG)) {
      console.group(`📁 ${label}`);
    }
  }

  groupEnd() {
    if (this.isEnabled && shouldLog(LOG_LEVELS.DEBUG)) {
      console.groupEnd();
    }
  }

  getConfig() {
    return {
      isEnabled: this.isEnabled,
      currentLevel: this.currentLevel,
      environment: process.env.NODE_ENV,
      logLevel: localStorage.getItem(STORAGE_KEYS.LOG_LEVEL) || process.env.ADDO_SHARE_LOG_LEVEL,
      loggingEnabled: localStorage.getItem(STORAGE_KEYS.ENABLE_LOGGING) || process.env.ADDO_SHARE_ENABLE_LOGGING,
      localStorageOverrides: {
        logLevel: localStorage.getItem(STORAGE_KEYS.LOG_LEVEL),
        enableLogging: localStorage.getItem(STORAGE_KEYS.ENABLE_LOGGING),
      },
    };
  }

  setLogLevel(level) {
    localStorage.setItem(STORAGE_KEYS.LOG_LEVEL, level);
    this.currentLevel = getLogLevel();
  }

  setLoggingEnabled(enabled) {
    localStorage.setItem(STORAGE_KEYS.ENABLE_LOGGING, enabled.toString());
    this.isEnabled = getLoggingEnabled();
  }

  resetToDefaults() {
    localStorage.removeItem(STORAGE_KEYS.LOG_LEVEL);
    localStorage.removeItem(STORAGE_KEYS.ENABLE_LOGGING);
    this.currentLevel = getLogLevel();
    this.isEnabled = getLoggingEnabled();
  }
}

export const logger = new Logger();
export { Logger, LOG_LEVELS, STORAGE_KEYS, APP_LOG_PREFIX };
export const createLogger = (config = {}) => new Logger(config);
export const isLoggingEnabled = () => logger.isEnabled;
export const getCurrentLogLevel = () => logger.currentLevel;
export const setLogLevel = (level) => logger.setLogLevel(level);
export const setLoggingEnabled = (enabled) => logger.setLoggingEnabled(enabled);
export const resetLoggingConfig = () => logger.resetToDefaults();

export default logger;
